// MyCourses.js
import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  SafeAreaView,
  StatusBar,
  ScrollView,
  FlatList,
  Image,
  Dimensions,
  TextInput,
  Alert,
  Platform,
  Animated,
  Modal,
  RefreshControl,
  ActivityIndicator,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { 
  MaterialCommunityIcons, 
  Ionicons, 
  FontAwesome5, 
  Feather,
  AntDesign,
  Entypo
} from '@expo/vector-icons';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import { LinearGradient } from 'expo-linear-gradient';
import { Video } from 'expo-video';
import { Audio } from 'expo-audio'; // if you need audio
import * as Progress from 'react-native-progress';
import { BlurView } from 'expo-blur';

const { width, height } = Dimensions.get('window');

const MyCourses = () => {
  const navigation = useNavigation();

  // Navigate only if the target screen exists; otherwise show a friendly notice
  // instead of throwing "not handled by any navigator".
  const REGISTERED_SCREENS = [
    'CourseContent', 'CourseAssignments', 'CertificateView',
    'Certificates', 'TodaySchedule', 'CourseCatalog',
  ];
  const safeNavigate = (screen, params, label) => {
    if (REGISTERED_SCREENS.includes(screen)) {
      navigation.navigate(screen, params);
    } else {
      Alert.alert(label || 'Coming Soon', 'This feature is coming soon.', [{ text: 'OK' }]);
    }
  };

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [enrolledCourses, setEnrolledCourses] = useState([]);
  const [completedCourses, setCompletedCourses] = useState([]);
  const [bookmarkedCourses, setBookmarkedCourses] = useState([]);
  const [activeTab, setActiveTab] = useState('enrolled'); // 'enrolled', 'completed', 'bookmarked'
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [showCourseDetails, setShowCourseDetails] = useState(false);
  const [showAddCourseModal, setShowAddCourseModal] = useState(false);
  const [newCourseCode, setNewCourseCode] = useState('');
  const [stats, setStats] = useState({
    totalCourses: 0,
    completedCourses: 0,
    inProgressCourses: 0,
    totalHours: 0,
    streakDays: 0,
    certificates: 0,
  });
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [playingVideoId, setPlayingVideoId] = useState(null);
  
  const scrollY = useRef(new Animated.Value(0)).current;
  const searchTimeoutRef = useRef(null);
  const fadeAnim = useRef(new Animated.Value(0)).current;

  // Animation for header
  const headerHeight = scrollY.interpolate({
    inputRange: [0, 100],
    outputRange: [180, 100],
    extrapolate: 'clamp',
  });

  const headerOpacity = scrollY.interpolate({
    inputRange: [0, 80],
    outputRange: [1, 0.8],
    extrapolate: 'clamp',
  });

  useFocusEffect(
    React.useCallback(() => {
      loadInitialData();
      startAnimations();
      return () => {
        if (searchTimeoutRef.current) {
          clearTimeout(searchTimeoutRef.current);
        }
      };
    }, [])
  );

  useEffect(() => {
    filterCourses();
  }, [searchQuery, enrolledCourses, completedCourses, bookmarkedCourses, activeTab]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      
      // Load preferences
      const darkModePref = await AsyncStorage.getItem('darkMode');
      if (darkModePref !== null) {
        setDarkMode(JSON.parse(darkModePref));
      }
      
      // Load all courses data
      await Promise.all([
        loadEnrolledCourses(),
        loadCompletedCourses(),
        loadBookmarkedCourses(),
        loadUserStats(),
      ]);
      
    } catch (error) {
      console.error('Error loading initial data:', error);
      Alert.alert('Error', 'Failed to load courses data');
    } finally {
      setLoading(false);
    }
  };

  const loadEnrolledCourses = async () => {
    try {
      const enrolled = await AsyncStorage.getItem('enrolledCourses');
      if (enrolled) {
        const courses = JSON.parse(enrolled);
        setEnrolledCourses(courses);
        setFilteredCourses(courses);
      } else {
        // Load mock data if no enrolled courses
        const mockCourses = getMockEnrolledCourses();
        setEnrolledCourses(mockCourses);
        setFilteredCourses(mockCourses);
        await AsyncStorage.setItem('enrolledCourses', JSON.stringify(mockCourses));
      }
    } catch (error) {
      console.error('Error loading enrolled courses:', error);
      // Fallback to mock data
      const mockCourses = getMockEnrolledCourses();
      setEnrolledCourses(mockCourses);
      setFilteredCourses(mockCourses);
    }
  };

  const getMockEnrolledCourses = () => {
    return [
      {
        id: 'C001',
        title: 'Introduction to Python for Engineers',
        subtitle: 'Programming fundamentals for engineering applications',
        instructor: 'Prof. Sarah Johnson',
        duration: '8 hours',
        totalLessons: 24,
        completedLessons: 16,
        rating: 4.8,
        students: 18500,
        level: 'Beginner',
        isFree: true,
        price: 0,
        color: '#3776AB',
        icon: 'language-python',
        progress: 65,
        category: 'computer',
        description: 'Learn Python programming fundamentals with engineering applications including data analysis, automation, and scientific computing.',
        modules: [
          { id: 'M1', title: 'Python Basics and Syntax', duration: '2 hours', completed: true, lessons: 8 },
          { id: 'M2', title: 'Data Structures for Engineering', duration: '2 hours', completed: true, lessons: 6 },
          { id: 'M3', title: 'NumPy and Scientific Computing', duration: '2 hours', completed: false, lessons: 5 },
          { id: 'M4', title: 'Engineering Applications', duration: '2 hours', completed: false, lessons: 5 },
        ],
        videoPreview: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4',
        thumbnail: 'https://images.unsplash.com/photo-1526379879527-8559ecfcaec7?w=400',
        startDate: '2024-01-15',
        endDate: '2024-03-15',
        lastAccessed: '2024-02-20',
        certificateAvailable: true,
        assignments: 5,
        completedAssignments: 3,
        quizzes: 3,
        completedQuizzes: 2,
        discussionPosts: 12,
        nextLesson: 'NumPy Arrays and Operations',
        nextLessonTime: '2:30 PM Today',
        deadline: '2024-03-10',
        timeSpent: '12h 30m',
        weeklyGoal: '5 hours',
        weeklyProgress: '3h 45m',
        difficulty: 'Easy',
        engagement: 'High',
      },
      {
        id: 'C002',
        title: 'Advanced Structural Analysis',
        subtitle: 'Finite element methods and dynamic analysis',
        instructor: 'Dr. Robert Williams',
        duration: '25 hours',
        totalLessons: 48,
        completedLessons: 19,
        rating: 4.9,
        students: 3200,
        level: 'Advanced',
        isFree: false,
        price: 149,
        color: '#0e4e96',
        icon: 'bridge',
        progress: 40,
        category: 'civil',
        description: 'Master advanced structural analysis techniques including finite element methods, dynamic analysis, and seismic design principles.',
        modules: [
          { id: 'M1', title: 'FEM Fundamentals', duration: '5 hours', completed: true, lessons: 12 },
          { id: 'M2', title: 'Dynamic Analysis', duration: '6 hours', completed: true, lessons: 14 },
          { id: 'M3', title: 'Seismic Design Principles', duration: '7 hours', completed: false, lessons: 16 },
          { id: 'M4', title: 'Advanced Applications', duration: '7 hours', completed: false, lessons: 18 },
        ],
        videoPreview: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ElephantsDream.mp4',
        thumbnail: 'https://images.unsplash.com/photo-1504917595217-d4dc5ebe6122?w=400',
        startDate: '2024-01-10',
        endDate: '2024-04-10',
        lastAccessed: '2024-02-18',
        certificateAvailable: true,
        assignments: 8,
        completedAssignments: 3,
        quizzes: 4,
        completedQuizzes: 1,
        discussionPosts: 8,
        nextLesson: 'Seismic Load Calculations',
        nextLessonTime: '4:00 PM Tomorrow',
        deadline: '2024-03-25',
        timeSpent: '18h 15m',
        weeklyGoal: '6 hours',
        weeklyProgress: '4h 20m',
        difficulty: 'Hard',
        engagement: 'Medium',
      },
      {
        id: 'C003',
        title: 'Machine Learning for Engineers',
        subtitle: 'AI applications in engineering systems',
        instructor: 'Dr. Alex Rodriguez',
        duration: '30 hours',
        totalLessons: 36,
        completedLessons: 29,
        rating: 4.8,
        students: 4500,
        level: 'Intermediate',
        isFree: false,
        price: 199,
        color: '#FF4081',
        icon: 'robot',
        progress: 80,
        category: 'computer',
        description: 'Apply machine learning techniques to solve complex engineering problems including predictive maintenance, quality control, and optimization.',
        modules: [
          { id: 'M1', title: 'ML Fundamentals for Engineers', duration: '6 hours', completed: true, lessons: 9 },
          { id: 'M2', title: 'Data Preprocessing', duration: '6 hours', completed: true, lessons: 8 },
          { id: 'M3', title: 'Predictive Modeling', duration: '9 hours', completed: true, lessons: 12 },
          { id: 'M4', title: 'Engineering Case Studies', duration: '9 hours', completed: false, lessons: 10 },
        ],
        videoPreview: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerJoyrides.mp4',
        thumbnail: 'https://images.unsplash.com/photo-1555949963-aa79dcee981c?w=400',
        startDate: '2023-11-05',
        endDate: '2024-02-05',
        lastAccessed: '2024-02-19',
        certificateAvailable: true,
        assignments: 6,
        completedAssignments: 5,
        quizzes: 3,
        completedQuizzes: 3,
        discussionPosts: 15,
        nextLesson: 'Case Study: Predictive Maintenance',
        nextLessonTime: '10:00 AM Today',
        deadline: '2024-02-28',
        timeSpent: '24h 45m',
        weeklyGoal: '8 hours',
        weeklyProgress: '6h 15m',
        difficulty: 'Medium',
        engagement: 'High',
      },
      {
        id: 'C004',
        title: 'Electrical Circuit Design',
        subtitle: 'Digital and analog circuit fundamentals',
        instructor: 'Prof. Michael Chen',
        duration: '15 hours',
        totalLessons: 30,
        completedLessons: 18,
        rating: 4.7,
        students: 2800,
        level: 'Intermediate',
        isFree: true,
        price: 0,
        color: '#FFD700',
        icon: 'flash',
        progress: 60,
        category: 'electrical',
        description: 'Comprehensive course on electrical circuit design covering both digital and analog circuits with practical lab exercises.',
        modules: [
          { id: 'M1', title: 'Circuit Fundamentals', duration: '4 hours', completed: true, lessons: 8 },
          { id: 'M2', title: 'Analog Circuits', duration: '5 hours', completed: true, lessons: 10 },
          { id: 'M3', title: 'Digital Circuits', duration: '3 hours', completed: false, lessons: 6 },
          { id: 'M4', title: 'PCB Design', duration: '3 hours', completed: false, lessons: 6 },
        ],
        videoPreview: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerBlazes.mp4',
        thumbnail: 'https://images.unsplash.com/photo-1544197150-b99a580bb7a8?w=400',
        startDate: '2024-02-01',
        endDate: '2024-04-01',
        lastAccessed: '2024-02-17',
        certificateAvailable: false,
        assignments: 4,
        completedAssignments: 2,
        quizzes: 2,
        completedQuizzes: 1,
        discussionPosts: 5,
        nextLesson: 'Digital Logic Gates',
        nextLessonTime: '3:00 PM Wednesday',
        deadline: '2024-03-15',
        timeSpent: '9h 20m',
        weeklyGoal: '4 hours',
        weeklyProgress: '2h 45m',
        difficulty: 'Medium',
        engagement: 'Low',
      },
    ];
  };

  const loadCompletedCourses = async () => {
    try {
      const completed = await AsyncStorage.getItem('completedCourses');
      if (completed) {
        setCompletedCourses(JSON.parse(completed));
      } else {
        // Mock completed courses
        const mockCompleted = [
          {
            id: 'C005',
            title: 'Engineering Mathematics',
            subtitle: 'Calculus, Linear Algebra, Differential Equations',
            instructor: 'Dr. James Wilson',
            duration: '20 hours',
            completedDate: '2024-01-20',
            grade: 'A',
            certificateId: 'CERT-001',
            color: '#9B30FF',
            icon: 'calculator',
            progress: 100,
          },
        ];
        setCompletedCourses(mockCompleted);
      }
    } catch (error) {
      console.error('Error loading completed courses:', error);
    }
  };

  const loadBookmarkedCourses = async () => {
    try {
      const bookmarks = await AsyncStorage.getItem('bookmarkedCourses');
      if (bookmarks) {
        const bookmarkedIds = JSON.parse(bookmarks);
        // Filter enrolled courses to get bookmarked ones
        const bookmarked = enrolledCourses.filter(course => 
          bookmarkedIds.includes(course.id)
        );
        setBookmarkedCourses(bookmarked);
      }
    } catch (error) {
      console.error('Error loading bookmarked courses:', error);
    }
  };

  const loadUserStats = async () => {
    try {
      const statsData = await AsyncStorage.getItem('courseStats');
      if (statsData) {
        setStats(JSON.parse(statsData));
      } else {
        // Calculate stats from enrolled courses
        const totalCourses = enrolledCourses.length;
        const completedCourses = enrolledCourses.filter(c => c.progress === 100).length;
        const inProgressCourses = enrolledCourses.filter(c => c.progress > 0 && c.progress < 100).length;
        const totalHours = enrolledCourses.reduce((sum, course) => {
          const hours = parseInt(course.duration) || 0;
          return sum + hours * (course.progress / 100);
        }, 0);
        
        const newStats = {
          totalCourses,
          completedCourses,
          inProgressCourses,
          totalHours: Math.round(totalHours),
          streakDays: 7,
          certificates: completedCourses,
        };
        
        setStats(newStats);
        await AsyncStorage.setItem('courseStats', JSON.stringify(newStats));
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const startAnimations = () => {
    Animated.timing(fadeAnim, {
      toValue: 1,
      duration: 800,
      useNativeDriver: true,
    }).start();
  };

  const onRefresh = async () => {
    setRefreshing(true);
    try {
      await loadInitialData();
    } catch (error) {
      console.error('Error refreshing:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const filterCourses = () => {
    let courses = [];
    switch (activeTab) {
      case 'enrolled':
        courses = enrolledCourses;
        break;
      case 'completed':
        courses = completedCourses;
        break;
      case 'bookmarked':
        courses = bookmarkedCourses;
        break;
      default:
        courses = enrolledCourses;
    }
    
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      courses = courses.filter(course =>
        String(course.title ?? '').toLowerCase().includes(query) ||
        String(course.subtitle ?? '').toLowerCase().includes(query) ||
        String(course.instructor ?? '').toLowerCase().includes(query) ||
        String(course.category ?? '').toLowerCase().includes(query)
      );
    }
    
    setFilteredCourses(courses);
  };

  const handleSearch = (text) => {
    setSearchQuery(text);
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      filterCourses();
    }, 300);
  };

  const enrollNewCourse = async () => {
    if (!newCourseCode.trim()) {
      Alert.alert('Error', 'Please enter a course code');
      return;
    }
    
    try {
      // Mock enrollment - in real app, this would validate with backend
      const newCourse = {
        id: `C00${enrolledCourses.length + 1}`,
        title: 'New Engineering Course',
        subtitle: 'Added via course code',
        instructor: 'New Instructor',
        duration: '10 hours',
        totalLessons: 20,
        completedLessons: 0,
        rating: 4.5,
        students: 1000,
        level: 'Beginner',
        isFree: false,
        price: 99,
        color: '#667eea',
        icon: 'book-open-variant',
        progress: 0,
        category: 'general',
        description: 'New course enrolled using course code.',
        modules: [],
        startDate: new Date().toISOString().split('T')[0],
        endDate: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        lastAccessed: new Date().toISOString().split('T')[0],
        certificateAvailable: true,
      };
      
      const updatedCourses = [...enrolledCourses, newCourse];
      setEnrolledCourses(updatedCourses);
      setNewCourseCode('');
      setShowAddCourseModal(false);
      
      await AsyncStorage.setItem('enrolledCourses', JSON.stringify(updatedCourses));
      
      // Update stats
      const newStats = {
        ...stats,
        totalCourses: updatedCourses.length,
        inProgressCourses: stats.inProgressCourses + 1,
      };
      setStats(newStats);
      await AsyncStorage.setItem('courseStats', JSON.stringify(newStats));
      
      Alert.alert('Success', 'Course enrolled successfully!');
      
    } catch (error) {
      console.error('Error enrolling course:', error);
      Alert.alert('Error', 'Failed to enroll in course');
    }
  };

  const markLessonComplete = async (courseId, moduleId, lessonId) => {
    try {
      const updatedCourses = enrolledCourses.map(course => {
        if (course.id === courseId) {
          // Update module completion
          const updatedModules = course.modules.map(module => {
            if (module.id === moduleId) {
              return { ...module, completed: true };
            }
            return module;
          });
          
          // Calculate new progress
          const completedModules = updatedModules.filter(m => m.completed).length;
          const totalModules = course.modules.length;
          const newProgress = Math.round((completedModules / totalModules) * 100);
          
          return {
            ...course,
            modules: updatedModules,
            progress: newProgress,
            completedLessons: course.completedLessons + 1,
          };
        }
        return course;
      });
      
      setEnrolledCourses(updatedCourses);
      await AsyncStorage.setItem('enrolledCourses', JSON.stringify(updatedCourses));
      
      // Update stats if course is completed
      const completedCourse = updatedCourses.find(c => c.id === courseId && c.progress === 100);
      if (completedCourse) {
        const newStats = {
          ...stats,
          completedCourses: stats.completedCourses + 1,
          inProgressCourses: stats.inProgressCourses - 1,
          certificates: stats.certificates + 1,
        };
        setStats(newStats);
        await AsyncStorage.setItem('courseStats', JSON.stringify(newStats));
        
        Alert.alert('Congratulations!', `You've completed ${completedCourse.title}!`);
      }
      
    } catch (error) {
      console.error('Error marking lesson complete:', error);
    }
  };

  const toggleBookmark = async (courseId) => {
    try {
      const bookmarks = await AsyncStorage.getItem('bookmarkedCourses') || '[]';
      const bookmarkedIds = JSON.parse(bookmarks);
      
      let newBookmarks;
      if (bookmarkedIds.includes(courseId)) {
        newBookmarks = bookmarkedIds.filter(id => id !== courseId);
        // Remove from bookmarked courses list
        setBookmarkedCourses(prev => prev.filter(c => c.id !== courseId));
      } else {
        newBookmarks = [...bookmarkedIds, courseId];
        // Add to bookmarked courses list
        const courseToBookmark = enrolledCourses.find(c => c.id === courseId);
        if (courseToBookmark) {
          setBookmarkedCourses(prev => [...prev, courseToBookmark]);
        }
      }
      
      await AsyncStorage.setItem('bookmarkedCourses', JSON.stringify(newBookmarks));
      
    } catch (error) {
      console.error('Error toggling bookmark:', error);
    }
  };

  const calculateTimeRemaining = (endDate) => {
    const end = new Date(endDate);
    const now = new Date();
    const diffTime = end - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? `${diffDays} days left` : 'Course ended';
  };

  const getProgressColor = (progress) => {
    if (progress >= 80) return '#10b981';
    if (progress >= 50) return '#f59e0b';
    return '#ef4444';
  };

  const renderLoading = () => (
    <View style={[styles.loadingContainer, { backgroundColor: darkMode ? '#1a202c' : '#f0f4f8' }]}>
      <ActivityIndicator size={36} color="#667eea" />
      <Text style={[styles.loadingText, { color: darkMode ? '#cbd5e0' : '#1f2937' }]}>
        Loading Your Courses...
      </Text>
    </View>
  );

  const renderHeader = () => (
    <Animated.View 
      style={[
        styles.header,
        {
          height: headerHeight,
          opacity: headerOpacity,
        },
      ]}
    >
      <LinearGradient
        colors={darkMode ? ['#2d3748', '#4a5568'] : ['#667eea', '#764ba2']}
        style={styles.headerGradient}
      >
        <View style={styles.headerTop}>
          <TouchableOpacity 
            style={styles.menuButton}
            onPress={() => navigation.toggleDrawer?.() || navigation.goBack()}
          >
            <MaterialCommunityIcons name="menu" size={24} color="#ffffff" />
          </TouchableOpacity>
          
          <Text style={styles.headerTitle}>📚 My Courses</Text>
          
          <View style={styles.headerActions}>
            <TouchableOpacity 
              style={styles.iconButton}
              onPress={() => setShowAddCourseModal(true)}
            >
              <MaterialCommunityIcons name="plus" size={24} color="#ffffff" />
            </TouchableOpacity>
            
            <TouchableOpacity 
              style={styles.iconButton}
              onPress={() => setShowStatsModal(true)}
            >
              <MaterialCommunityIcons name="chart-bar" size={24} color="#ffffff" />
            </TouchableOpacity>
          </View>
        </View>
        
        <Text style={styles.headerSubtitle}>
          Track your learning progress and achievements
        </Text>
        
        <View style={styles.statsContainer}>
          <TouchableOpacity 
            style={styles.statItem}
            onPress={() => setActiveTab('enrolled')}
          >
            <Text style={styles.statNumber}>{stats.totalCourses}</Text>
            <Text style={styles.statLabel}>Enrolled</Text>
          </TouchableOpacity>
          
          <TouchableOpacity 
            style={styles.statItem}
            onPress={() => setActiveTab('inProgress')}
          >
            <Text style={styles.statNumber}>{stats.inProgressCourses}</Text>
            <Text style={styles.statLabel}>In Progress</Text>
          </TouchableOpacity>
          
          <TouchableOpacity 
            style={styles.statItem}
            onPress={() => setActiveTab('completed')}
          >
            <Text style={styles.statNumber}>{stats.completedCourses}</Text>
            <Text style={styles.statLabel}>Completed</Text>
          </TouchableOpacity>
          
          <TouchableOpacity 
            style={styles.statItem}
            onPress={() => navigation.navigate('Certificates')}
          >
            <Text style={styles.statNumber}>{stats.certificates}</Text>
            <Text style={styles.statLabel}>Certificates</Text>
          </TouchableOpacity>
        </View>
      </LinearGradient>
    </Animated.View>
  );

  const renderSearchBar = () => (
    <View style={[styles.searchContainer, { 
      backgroundColor: darkMode ? '#2d3748' : '#ffffff',
      shadowColor: darkMode ? '#000' : '#667eea',
    }]}>
      <View style={styles.searchInputContainer}>
        <MaterialCommunityIcons 
          name="magnify" 
          size={20} 
          color={darkMode ? '#a0aec0' : '#6b7280'} 
        />
        <TextInput
          style={[styles.searchInput, { color: darkMode ? '#f7fafc' : '#1f2937' }]}
          placeholder="Search courses, instructors..."
          placeholderTextColor={darkMode ? '#a0aec0' : '#6b7280'}
          value={searchQuery}
          onChangeText={handleSearch}
        />
        {searchQuery.length > 0 && (
          <TouchableOpacity onPress={() => setSearchQuery('')}>
            <MaterialCommunityIcons 
              name="close-circle" 
              size={18} 
              color={darkMode ? '#a0aec0' : '#6b7280'} 
            />
          </TouchableOpacity>
        )}
      </View>
      
      <TouchableOpacity
        style={[styles.filterButton, { backgroundColor: darkMode ? '#4a5568' : '#f3f4f6' }]}
        onPress={() => safeNavigate('CourseFilters', null, 'Filters')}
      >
        <MaterialCommunityIcons 
          name="filter-variant" 
          size={18} 
          color={darkMode ? '#ffffff' : '#1f2937'} 
        />
      </TouchableOpacity>
    </View>
  );

  const renderTabs = () => (
    <View style={styles.tabsContainer}>
      <ScrollView horizontal showsHorizontalScrollIndicator={false}>
        {[
          { id: 'enrolled', label: 'Enrolled', icon: 'book-open-variant', count: enrolledCourses.length },
          { id: 'inProgress', label: 'In Progress', icon: 'progress-clock', count: stats.inProgressCourses },
          { id: 'completed', label: 'Completed', icon: 'check-circle', count: stats.completedCourses },
          { id: 'bookmarked', label: 'Bookmarked', icon: 'bookmark', count: bookmarkedCourses.length },
          { id: 'upcoming', label: 'Upcoming', icon: 'calendar-clock', count: 2 },
        ].map((tab) => (
          <TouchableOpacity
            key={tab.id}
            style={[
              styles.tabButton,
              { backgroundColor: darkMode ? '#2d3748' : '#ffffff' },
              activeTab === tab.id && { backgroundColor: '#667eea' }
            ]}
            onPress={() => setActiveTab(tab.id)}
          >
            <MaterialCommunityIcons 
              name={tab.icon} 
              size={18} 
              color={activeTab === tab.id ? '#ffffff' : (darkMode ? '#a0aec0' : '#6b7280')} 
            />
            <Text style={[
              styles.tabText,
              { color: activeTab === tab.id ? '#ffffff' : (darkMode ? '#cbd5e0' : '#4b5563') }
            ]}>
              {tab.label}
            </Text>
            {tab.count > 0 && (
              <View style={[
                styles.tabCount,
                { backgroundColor: activeTab === tab.id ? 'rgba(255,255,255,0.2)' : (darkMode ? '#4a5568' : '#e5e7eb') }
              ]}>
                <Text style={[
                  styles.tabCountText,
                  { color: activeTab === tab.id ? '#ffffff' : (darkMode ? '#a0aec0' : '#6b7280') }
                ]}>
                  {tab.count}
                </Text>
              </View>
            )}
          </TouchableOpacity>
        ))}
      </ScrollView>
    </View>
  );

  const renderCourseCard = (course) => (
    <Animated.View 
      style={[
        styles.courseCard,
        {
          opacity: fadeAnim,
          transform: [{
            translateY: fadeAnim.interpolate({
              inputRange: [0, 1],
              outputRange: [50, 0],
            }),
          }],
        },
      ]}
    >
      <TouchableOpacity
        style={[styles.courseCardContent, { backgroundColor: darkMode ? '#2d3748' : '#ffffff' }]}
        onPress={() => {
          setSelectedCourse(course);
          setShowCourseDetails(true);
        }}
        activeOpacity={0.8}
      >
        <View style={styles.courseHeader}>
          <LinearGradient
            colors={[course.color, `${course.color}80`]}
            style={styles.courseIcon}
          >
            <MaterialCommunityIcons name={course.icon} size={24} color="#ffffff" />
          </LinearGradient>
          
          <View style={styles.courseTitleContainer}>
            <Text style={[styles.courseTitle, { color: darkMode ? '#f7fafc' : '#1f2937' }]} numberOfLines={2}>
              {course.title}
            </Text>
            <Text style={[styles.courseInstructor, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
              {course.instructor}
            </Text>
          </View>
          
          <TouchableOpacity 
            style={styles.bookmarkButton}
            onPress={() => toggleBookmark(course.id)}
          >
            <MaterialCommunityIcons
              name={bookmarkedCourses.some(c => c.id === course.id) ? "bookmark" : "bookmark-outline"}
              size={20}
              color={bookmarkedCourses.some(c => c.id === course.id) ? "#f59e0b" : (darkMode ? "#a0aec0" : "#6b7280")}
            />
          </TouchableOpacity>
        </View>
        
        <Text style={[styles.courseSubtitle, { color: darkMode ? '#cbd5e0' : '#4b5563' }]} numberOfLines={2}>
          {course.subtitle}
        </Text>
        
        <View style={styles.courseProgressSection}>
          <View style={styles.progressHeader}>
            <Text style={[styles.progressLabel, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
              Progress: {course.progress}%
            </Text>
            <Text style={[styles.progressTime, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
              {calculateTimeRemaining(course.endDate)}
            </Text>
          </View>
          
          <View style={styles.progressBarContainer}>
            <View style={[styles.progressBarBackground, { backgroundColor: darkMode ? '#4a5568' : '#e5e7eb' }]}>
              <View 
                style={[
                  styles.progressBarFill, 
                  { 
                    width: `${course.progress}%`,
                    backgroundColor: getProgressColor(course.progress),
                  }
                ]} 
              />
            </View>
          </View>
          
          <View style={styles.progressDetails}>
            <View style={styles.progressDetail}>
              <MaterialCommunityIcons name="play-circle" size={14} color={darkMode ? '#a0aec0' : '#6b7280'} />
              <Text style={[styles.progressDetailText, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                {course.completedLessons}/{course.totalLessons} lessons
              </Text>
            </View>
            <View style={styles.progressDetail}>
              <MaterialCommunityIcons name="clock-outline" size={14} color={darkMode ? '#a0aec0' : '#6b7280'} />
              <Text style={[styles.progressDetailText, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                {course.timeSpent || '0h 0m'}
              </Text>
            </View>
          </View>
        </View>
        
        <View style={styles.courseActions}>
          <TouchableOpacity 
            style={[styles.courseActionButton, { backgroundColor: darkMode ? '#4a5568' : '#f3f4f6' }]}
            onPress={() => navigation.navigate('CourseContent', { course })}
          >
            <MaterialCommunityIcons name="play-circle" size={16} color="#667eea" />
            <Text style={[styles.courseActionText, { color: '#667eea' }]}>
              Continue
            </Text>
          </TouchableOpacity>
          
          <TouchableOpacity 
            style={[styles.courseActionButton, { backgroundColor: darkMode ? '#4a5568' : '#f3f4f6' }]}
            onPress={() => navigation.navigate('CourseAssignments', { course })}
          >
            <MaterialCommunityIcons name="clipboard-text" size={16} color="#10b981" />
            <Text style={[styles.courseActionText, { color: '#10b981' }]}>
              Assignments
            </Text>
          </TouchableOpacity>
          
          <TouchableOpacity 
            style={[styles.courseActionButton, { backgroundColor: darkMode ? '#4a5568' : '#f3f4f6' }]}
            onPress={() => safeNavigate('CourseDiscussion', { course }, 'Discussion')}
          >
            <MaterialCommunityIcons name="message-text" size={16} color="#f59e0b" />
            <Text style={[styles.courseActionText, { color: '#f59e0b' }]}>
              Discuss
            </Text>
          </TouchableOpacity>
        </View>
        
        {course.nextLesson && (
          <View style={[styles.nextLessonContainer, { backgroundColor: darkMode ? '#4a5568' : '#f3f4f6' }]}>
            <MaterialCommunityIcons name="clock-alert" size={16} color="#667eea" />
            <Text style={[styles.nextLessonText, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
              Next: {course.nextLesson} • {course.nextLessonTime}
            </Text>
          </View>
        )}
      </TouchableOpacity>
    </Animated.View>
  );

  const renderCompletedCourseCard = (course) => (
    <Animated.View 
      style={[
        styles.courseCard,
        {
          opacity: fadeAnim,
          transform: [{
            translateY: fadeAnim.interpolate({
              inputRange: [0, 1],
              outputRange: [50, 0],
            }),
          }],
        },
      ]}
    >
      <View style={[styles.completedCourseCard, { backgroundColor: darkMode ? '#2d3748' : '#ffffff' }]}>
        <View style={styles.completedCourseHeader}>
          <LinearGradient
            colors={[course.color, `${course.color}80`]}
            style={styles.courseIcon}
          >
            <MaterialCommunityIcons name={course.icon} size={24} color="#ffffff" />
          </LinearGradient>
          
          <View style={styles.courseTitleContainer}>
            <Text style={[styles.courseTitle, { color: darkMode ? '#f7fafc' : '#1f2937' }]} numberOfLines={2}>
              {course.title}
            </Text>
            <Text style={[styles.courseInstructor, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
              {course.instructor}
            </Text>
          </View>
          
          <View style={styles.completedBadge}>
            <MaterialCommunityIcons name="check-circle" size={20} color="#10b981" />
          </View>
        </View>
        
        <View style={styles.completionDetails}>
          <View style={styles.completionDetail}>
            <MaterialCommunityIcons name="calendar-check" size={16} color={darkMode ? '#a0aec0' : '#6b7280'} />
            <Text style={[styles.completionDetailText, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
              Completed: {course.completedDate}
            </Text>
          </View>
          
          <View style={styles.completionDetail}>
            <MaterialCommunityIcons name="star" size={16} color="#fbbf24" />
            <Text style={[styles.completionDetailText, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
              Grade: {course.grade}
            </Text>
          </View>
          
          {course.certificateId && (
            <View style={styles.completionDetail}>
              <MaterialCommunityIcons name="certificate" size={16} color="#667eea" />
              <Text style={[styles.completionDetailText, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
                Certificate: {course.certificateId}
              </Text>
            </View>
          )}
        </View>
        
        <View style={styles.completedActions}>
          <TouchableOpacity 
            style={[styles.completedActionButton, { backgroundColor: '#667eea' }]}
            onPress={() => navigation.navigate('CertificateView', { course })}
          >
            <MaterialCommunityIcons name="certificate" size={16} color="#ffffff" />
            <Text style={[styles.completedActionText, { color: '#ffffff' }]}>
              View Certificate
            </Text>
          </TouchableOpacity>
          
          <TouchableOpacity 
            style={[styles.completedActionButton, { backgroundColor: darkMode ? '#4a5568' : '#f3f4f6' }]}
            onPress={() => safeNavigate('CourseReview', { course }, 'Course Review')}
          >
            <MaterialCommunityIcons name="star" size={16} color="#f59e0b" />
            <Text style={[styles.completedActionText, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
              Write Review
            </Text>
          </TouchableOpacity>
        </View>
      </View>
    </Animated.View>
  );

  const renderEmptyState = () => {
    let message = '';
    let icon = 'book-open-variant';
    let actionText = '';
    let onAction = null;
    
    switch (activeTab) {
      case 'enrolled':
        message = 'You haven\'t enrolled in any courses yet';
        icon = 'book-plus';
        actionText = 'Browse Courses';
        onAction = () => navigation.navigate('CourseCatalog');
        break;
      case 'completed':
        message = 'No completed courses yet';
        icon = 'trophy-outline';
        actionText = 'View Enrolled Courses';
        onAction = () => setActiveTab('enrolled');
        break;
      case 'bookmarked':
        message = 'No bookmarked courses';
        icon = 'bookmark-outline';
        actionText = 'Browse Courses';
        onAction = () => navigation.navigate('CourseCatalog');
        break;
      default:
        message = 'No courses found';
        icon = 'book-search';
        actionText = 'Search Courses';
        onAction = () => navigation.navigate('CourseCatalog');
    }
    
    return (
      <View style={styles.emptyState}>
        <MaterialCommunityIcons name={icon} size={64} color={darkMode ? '#4a5568' : '#d1d5db'} />
        <Text style={[styles.emptyStateTitle, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
          {message}
        </Text>
        <Text style={[styles.emptyStateText, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
          {searchQuery ? 'Try a different search term' : 'Start your learning journey today'}
        </Text>
        
        {onAction && (
          <TouchableOpacity
            style={styles.emptyStateButton}
            onPress={onAction}
          >
            <Text style={styles.emptyStateButtonText}>{actionText}</Text>
          </TouchableOpacity>
        )}
        
        {!searchQuery && activeTab === 'enrolled' && (
          <TouchableOpacity
            style={[styles.emptyStateButton, styles.addCourseButton]}
            onPress={() => setShowAddCourseModal(true)}
          >
            <MaterialCommunityIcons name="plus" size={20} color="#ffffff" />
            <Text style={styles.emptyStateButtonText}>Add Course by Code</Text>
          </TouchableOpacity>
        )}
      </View>
    );
  };

  const renderCourseDetailsModal = () => (
    <Modal
      visible={showCourseDetails}
      animationType="slide"
      onRequestClose={() => setShowCourseDetails(false)}
    >
      {selectedCourse && (
        <SafeAreaView style={[styles.modalContainer, { backgroundColor: darkMode ? '#1a202c' : '#ffffff' }]}>
          <View style={[styles.modalHeader, { backgroundColor: darkMode ? '#2d3748' : '#f3f4f6' }]}>
            <TouchableOpacity onPress={() => setShowCourseDetails(false)}>
              <MaterialCommunityIcons name="close" size={24} color={darkMode ? '#ffffff' : '#1f2937'} />
            </TouchableOpacity>
            
            <Text style={[styles.modalTitle, { color: darkMode ? '#ffffff' : '#1f2937' }]} numberOfLines={1}>
              {selectedCourse.title}
            </Text>
            
            <TouchableOpacity onPress={() => toggleBookmark(selectedCourse.id)}>
              <MaterialCommunityIcons
                name={bookmarkedCourses.some(c => c.id === selectedCourse.id) ? "bookmark" : "bookmark-outline"}
                size={24}
                color={darkMode ? '#ffffff' : '#1f2937'}
              />
            </TouchableOpacity>
          </View>
          
          <ScrollView style={styles.modalContent} showsVerticalScrollIndicator={false}>
            <View style={styles.courseOverview}>
              <LinearGradient
                colors={[selectedCourse.color, `${selectedCourse.color}80`]}
                style={styles.modalCourseIcon}
              >
                <MaterialCommunityIcons name={selectedCourse.icon} size={40} color="#ffffff" />
              </LinearGradient>
              
              <Text style={[styles.modalCourseTitle, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
                {selectedCourse.title}
              </Text>
              <Text style={[styles.modalCourseInstructor, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                Instructor: {selectedCourse.instructor}
              </Text>
              
              <View style={styles.modalProgressContainer}>
                <Text style={[styles.modalProgressLabel, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
                  Your Progress: {selectedCourse.progress}%
                </Text>
                <View style={[styles.modalProgressBar, { backgroundColor: darkMode ? '#4a5568' : '#e5e7eb' }]}>
                  <View 
                    style={[
                      styles.modalProgressFill, 
                      { 
                        width: `${selectedCourse.progress}%`,
                        backgroundColor: getProgressColor(selectedCourse.progress),
                      }
                    ]} 
                  />
                </View>
              </View>
            </View>
            
            <View style={styles.courseStats}>
              <View style={styles.statRow}>
                <View style={[styles.statBox, { backgroundColor: darkMode ? '#2d3748' : '#f3f4f6' }]}>
                  <MaterialCommunityIcons name="play-circle" size={20} color="#667eea" />
                  <Text style={[styles.statNumber, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
                    {selectedCourse.completedLessons}/{selectedCourse.totalLessons}
                  </Text>
                  <Text style={[styles.statLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>Lessons</Text>
                </View>
                
                <View style={[styles.statBox, { backgroundColor: darkMode ? '#2d3748' : '#f3f4f6' }]}>
                  <MaterialCommunityIcons name="clock-outline" size={20} color="#10b981" />
                  <Text style={[styles.statNumber, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
                    {selectedCourse.timeSpent || '0h 0m'}
                  </Text>
                  <Text style={[styles.statLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>Time Spent</Text>
                </View>
                
                <View style={[styles.statBox, { backgroundColor: darkMode ? '#2d3748' : '#f3f4f6' }]}>
                  <MaterialCommunityIcons name="clipboard-text" size={20} color="#f59e0b" />
                  <Text style={[styles.statNumber, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
                    {selectedCourse.completedAssignments || 0}/{selectedCourse.assignments || 0}
                  </Text>
                  <Text style={[styles.statLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>Assignments</Text>
                </View>
              </View>
            </View>
            
            <View style={styles.modulesSection}>
              <Text style={[styles.sectionTitle, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
                Course Modules
              </Text>
              
              {selectedCourse.modules?.map((module, index) => (
                <TouchableOpacity
                  key={module.id}
                  style={[styles.moduleItem, { backgroundColor: darkMode ? '#2d3748' : '#f3f4f6' }]}
                  onPress={() => safeNavigate('ModuleContent', { course: selectedCourse, module }, 'Module Content')}
                >
                  <View style={styles.moduleHeader}>
                    <View style={styles.moduleNumber}>
                      <Text style={[styles.moduleNumberText, { color: '#ffffff' }]}>
                        {index + 1}
                      </Text>
                    </View>
                    
                    <View style={styles.moduleInfo}>
                      <Text style={[styles.moduleTitle, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
                        {module.title}
                      </Text>
                      <Text style={[styles.moduleDuration, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                        {module.duration} • {module.lessons || 0} lessons
                      </Text>
                    </View>
                    
                    {module.completed ? (
                      <View style={styles.completedIndicator}>
                        <MaterialCommunityIcons name="check-circle" size={20} color="#10b981" />
                      </View>
                    ) : (
                      <TouchableOpacity
                        style={styles.startButton}
                        onPress={() => safeNavigate('ModuleContent', { course: selectedCourse, module }, 'Module Content')}
                      >
                        <MaterialCommunityIcons name="play-circle" size={20} color="#667eea" />
                      </TouchableOpacity>
                    )}
                  </View>
                  
                  {!module.completed && (
                    <TouchableOpacity
                      style={[styles.markCompleteButton, { backgroundColor: darkMode ? '#4a5568' : '#e5e7eb' }]}
                      onPress={() => markLessonComplete(selectedCourse.id, module.id, 'L1')}
                    >
                      <Text style={[styles.markCompleteText, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
                        Mark as Complete
                      </Text>
                    </TouchableOpacity>
                  )}
                </TouchableOpacity>
              ))}
            </View>
            
            <View style={styles.upcomingSection}>
              <Text style={[styles.sectionTitle, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
                Upcoming Schedule
              </Text>
              
              <View style={[styles.upcomingItem, { backgroundColor: darkMode ? '#2d3748' : '#f3f4f6' }]}>
                <MaterialCommunityIcons name="clock-alert" size={20} color="#667eea" />
                <View style={styles.upcomingInfo}>
                  <Text style={[styles.upcomingTitle, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
                    {selectedCourse.nextLesson || 'No upcoming lessons'}
                  </Text>
                  <Text style={[styles.upcomingTime, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                    {selectedCourse.nextLessonTime || 'Schedule not set'}
                  </Text>
                </View>
              </View>
              
              <View style={[styles.deadlineItem, { backgroundColor: darkMode ? '#2d3748' : '#f3f4f6' }]}>
                <MaterialCommunityIcons name="calendar-clock" size={20} color="#ef4444" />
                <View style={styles.upcomingInfo}>
                  <Text style={[styles.upcomingTitle, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
                    Course Deadline
                  </Text>
                  <Text style={[styles.upcomingTime, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                    {selectedCourse.deadline} • {calculateTimeRemaining(selectedCourse.deadline)}
                  </Text>
                </View>
              </View>
            </View>
          </ScrollView>
          
          <View style={[styles.modalActions, { backgroundColor: darkMode ? '#2d3748' : '#f3f4f6' }]}>
            <TouchableOpacity
              style={[styles.modalActionButton, styles.continueButton]}
              onPress={() => {
                setShowCourseDetails(false);
                navigation.navigate('CourseContent', { course: selectedCourse });
              }}
            >
              <MaterialCommunityIcons name="play-circle" size={20} color="#ffffff" />
              <Text style={styles.continueButtonText}>Continue Learning</Text>
            </TouchableOpacity>
            
            <TouchableOpacity
              style={[styles.modalActionButton, styles.resourcesButton]}
              onPress={() => safeNavigate('CourseResources', { course: selectedCourse }, 'Course Resources')}
            >
              <MaterialCommunityIcons name="folder-download" size={20} color="#ffffff" />
              <Text style={styles.resourcesButtonText}>Resources</Text>
            </TouchableOpacity>
          </View>
        </SafeAreaView>
      )}
    </Modal>
  );

  const renderAddCourseModal = () => (
    <Modal
      visible={showAddCourseModal}
      animationType="slide"
      transparent={true}
      onRequestClose={() => setShowAddCourseModal(false)}
    >
      <TouchableOpacity 
        style={styles.modalOverlay}
        activeOpacity={1}
        onPress={() => setShowAddCourseModal(false)}
      >
        <View style={[styles.addCourseModal, { backgroundColor: darkMode ? '#2d3748' : '#ffffff' }]}>
          <View style={styles.addCourseHeader}>
            <Text style={[styles.addCourseTitle, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
              Add New Course
            </Text>
            <TouchableOpacity onPress={() => setShowAddCourseModal(false)}>
              <MaterialCommunityIcons name="close" size={24} color={darkMode ? '#ffffff' : '#1f2937'} />
            </TouchableOpacity>
          </View>
          
          <View style={styles.addCourseContent}>
            <MaterialCommunityIcons name="qrcode-scan" size={64} color="#667eea" />
            <Text style={[styles.addCourseSubtitle, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
              Enter Course Code or Scan QR Code
            </Text>
            
            <TextInput
              style={[styles.courseCodeInput, { 
                color: darkMode ? '#ffffff' : '#1f2937',
                backgroundColor: darkMode ? '#4a5568' : '#f3f4f6',
                borderColor: darkMode ? '#4a5568' : '#d1d5db'
              }]}
              placeholder="Enter course code (e.g., ENG101)"
              placeholderTextColor={darkMode ? '#a0aec0' : '#6b7280'}
              value={newCourseCode}
              onChangeText={setNewCourseCode}
              autoCapitalize="characters"
            />
            
            <Text style={[styles.addCourseNote, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
              Course codes are provided by your instructor or institution
            </Text>
          </View>
          
          <View style={styles.addCourseActions}>
            <TouchableOpacity
              style={[styles.addCourseActionButton, styles.scanButton]}
              onPress={() => safeNavigate('QRScanner', null, 'QR Scanner')}
            >
              <MaterialCommunityIcons name="qrcode-scan" size={20} color="#ffffff" />
              <Text style={styles.scanButtonText}>Scan QR Code</Text>
            </TouchableOpacity>
            
            <TouchableOpacity
              style={[styles.addCourseActionButton, styles.enrollButton, !newCourseCode.trim() && styles.enrollButtonDisabled]}
              onPress={enrollNewCourse}
              disabled={!newCourseCode.trim()}
            >
              <MaterialCommunityIcons name="check-circle" size={20} color="#ffffff" />
              <Text style={styles.enrollButtonText}>Enroll Now</Text>
            </TouchableOpacity>
          </View>
        </View>
      </TouchableOpacity>
    </Modal>
  );

  const renderStatsModal = () => (
    <Modal
      visible={showStatsModal}
      animationType="slide"
      transparent={true}
      onRequestClose={() => setShowStatsModal(false)}
    >
      <TouchableOpacity 
        style={styles.modalOverlay}
        activeOpacity={1}
        onPress={() => setShowStatsModal(false)}
      >
        <View style={[styles.statsModal, { backgroundColor: darkMode ? '#2d3748' : '#ffffff' }]}>
          <View style={styles.statsHeader}>
            <Text style={[styles.statsTitle, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
              📊 Learning Statistics
            </Text>
            <TouchableOpacity onPress={() => setShowStatsModal(false)}>
              <MaterialCommunityIcons name="close" size={24} color={darkMode ? '#ffffff' : '#1f2937'} />
            </TouchableOpacity>
          </View>
          
          <ScrollView style={styles.statsContent} showsVerticalScrollIndicator={false}>
            <View style={styles.overallStats}>
              <LinearGradient
                colors={['#667eea', '#764ba2']}
                style={styles.overallStatsCard}
              >
                <Text style={styles.overallStatsTitle}>Overall Progress</Text>
                <Text style={styles.overallStatsPercentage}>
                  {Math.round((stats.completedCourses / Math.max(stats.totalCourses, 1)) * 100)}%
                </Text>
                <Text style={styles.overallStatsSubtitle}>
                  {stats.completedCourses} of {stats.totalCourses} courses completed
                </Text>
              </LinearGradient>
            </View>
            
            <View style={styles.detailedStats}>
              <View style={styles.statRow}>
                <View style={[styles.detailedStat, { backgroundColor: darkMode ? '#4a5568' : '#f3f4f6' }]}>
                  <MaterialCommunityIcons name="clock-outline" size={24} color="#667eea" />
                  <Text style={[styles.detailedStatNumber, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
                    {stats.totalHours}h
                  </Text>
                  <Text style={[styles.detailedStatLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                    Total Learning Time
                  </Text>
                </View>
                
                <View style={[styles.detailedStat, { backgroundColor: darkMode ? '#4a5568' : '#f3f4f6' }]}>
                  <MaterialCommunityIcons name="fire" size={24} color="#ef4444" />
                  <Text style={[styles.detailedStatNumber, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
                    {stats.streakDays}
                  </Text>
                  <Text style={[styles.detailedStatLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                    Day Streak
                  </Text>
                </View>
              </View>
              
              <View style={styles.statRow}>
                <View style={[styles.detailedStat, { backgroundColor: darkMode ? '#4a5568' : '#f3f4f6' }]}>
                  <MaterialCommunityIcons name="certificate" size={24} color="#10b981" />
                  <Text style={[styles.detailedStatNumber, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
                    {stats.certificates}
                  </Text>
                  <Text style={[styles.detailedStatLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                    Certificates Earned
                  </Text>
                </View>
                
                <View style={[styles.detailedStat, { backgroundColor: darkMode ? '#4a5568' : '#f3f4f6' }]}>
                  <MaterialCommunityIcons name="trending-up" size={24} color="#f59e0b" />
                  <Text style={[styles.detailedStatNumber, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
                    {stats.inProgressCourses}
                  </Text>
                  <Text style={[styles.detailedStatLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                    Active Courses
                  </Text>
                </View>
              </View>
            </View>
            
            <View style={styles.weeklyProgress}>
              <Text style={[styles.sectionTitle, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
                Weekly Progress
              </Text>
              
              {enrolledCourses.map(course => (
                <View key={course.id} style={styles.weeklyCourse}>
                  <Text style={[styles.weeklyCourseTitle, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
                    {course.title}
                  </Text>
                  <View style={styles.weeklyProgressBar}>
                    <View style={[styles.weeklyProgressFill, { 
                      width: `${(parseInt(course.weeklyProgress || '0') / parseInt(course.weeklyGoal || '1')) * 100}%`,
                      backgroundColor: course.color,
                    }]} />
                  </View>
                  <Text style={[styles.weeklyProgressText, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                    {course.weeklyProgress || '0h'} / {course.weeklyGoal || '0h'}
                  </Text>
                </View>
              ))}
            </View>
            
            <TouchableOpacity
              style={styles.exportStatsButton}
              onPress={() => safeNavigate('ExportStats', null, 'Export Stats')}
            >
              <MaterialCommunityIcons name="download" size={20} color="#667eea" />
              <Text style={styles.exportStatsText}>Export Learning Report</Text>
            </TouchableOpacity>
          </ScrollView>
        </View>
      </TouchableOpacity>
    </Modal>
  );

  const renderQuickActions = () => (
    <View style={styles.quickActions}>
      <TouchableOpacity
        style={[styles.quickAction, { backgroundColor: darkMode ? '#2d3748' : '#ffffff' }]}
        onPress={() => navigation.navigate('TodaySchedule')}
      >
        <MaterialCommunityIcons name="calendar-today" size={20} color="#667eea" />
        <Text style={[styles.quickActionText, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
          Today's Schedule
        </Text>
      </TouchableOpacity>
      
      <TouchableOpacity
        style={[styles.quickAction, { backgroundColor: darkMode ? '#2d3748' : '#ffffff' }]}
        onPress={() => safeNavigate('UpcomingDeadlines', null, 'Upcoming Deadlines')}
      >
        <MaterialCommunityIcons name="alert-circle" size={20} color="#ef4444" />
        <Text style={[styles.quickActionText, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
          Upcoming Deadlines
        </Text>
      </TouchableOpacity>
      
      <TouchableOpacity
        style={[styles.quickAction, { backgroundColor: darkMode ? '#2d3748' : '#ffffff' }]}
        onPress={() => safeNavigate('RecommendedCourses', null, 'Recommended Courses')}
      >
        <MaterialCommunityIcons name="lightbulb-on" size={20} color="#f59e0b" />
        <Text style={[styles.quickActionText, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
          Recommended
        </Text>
      </TouchableOpacity>
    </View>
  );

  const styles = StyleSheet.create({
    safeArea: {
      flex: 1,
      backgroundColor: '#667eea',
    },
    container: {
      flex: 1,
      backgroundColor: '#f0f4f8',
    },
    loadingContainer: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
    },
    loadingText: {
      fontSize: 18,
      fontWeight: '600',
      marginTop: 16,
    },
    header: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      zIndex: 100,
      overflow: 'hidden',
    },
    headerGradient: {
      paddingHorizontal: 20,
      paddingTop: Platform.OS === 'ios' ? 50 : 20,
      paddingBottom: 20,
    },
    headerTop: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: 16,
    },
    menuButton: {
      padding: 8,
    },
    headerTitle: {
      fontSize: 24,
      fontWeight: 'bold',
      color: '#ffffff',
      flex: 1,
      textAlign: 'center',
      marginHorizontal: 16,
    },
    headerActions: {
      flexDirection: 'row',
      alignItems: 'center',
    },
    iconButton: {
      padding: 8,
      marginLeft: 8,
    },
    headerSubtitle: {
      fontSize: 14,
      color: 'rgba(255, 255, 255, 0.9)',
      textAlign: 'center',
      marginBottom: 16,
    },
    statsContainer: {
      flexDirection: 'row',
      justifyContent: 'space-around',
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
      borderRadius: 16,
      padding: 12,
    },
    statItem: {
      alignItems: 'center',
    },
    statNumber: {
      fontSize: 18,
      fontWeight: 'bold',
      color: '#ffffff',
      marginBottom: 4,
    },
    statLabel: {
      fontSize: 12,
      color: 'rgba(255, 255, 255, 0.8)',
    },
    searchContainer: {
      position: 'absolute',
      top: 160,
      left: 20,
      right: 20,
      zIndex: 99,
      borderRadius: 16,
      padding: 16,
      flexDirection: 'row',
      alignItems: 'center',
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 8,
    },
    searchInputContainer: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: darkMode ? '#4a5568' : '#f3f4f6',
      borderRadius: 12,
      paddingHorizontal: 16,
      paddingVertical: 12,
      marginRight: 12,
    },
    searchInput: {
      flex: 1,
      fontSize: 16,
      marginHorizontal: 12,
    },
    filterButton: {
      padding: 12,
      borderRadius: 12,
    },
    tabsContainer: {
      paddingHorizontal: 20,
      marginTop: 120,
      marginBottom: 16,
    },
    tabButton: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingHorizontal: 16,
      paddingVertical: 10,
      borderRadius: 20,
      marginRight: 8,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 4,
      elevation: 2,
    },
    tabText: {
      fontSize: 14,
      fontWeight: '500',
      marginLeft: 6,
    },
    tabCount: {
      marginLeft: 6,
      paddingHorizontal: 6,
      paddingVertical: 2,
      borderRadius: 10,
    },
    tabCountText: {
      fontSize: 12,
      fontWeight: '600',
    },
    scrollView: {
      flex: 1,
      marginTop: 180,
    },
    contentContainer: {
      paddingBottom: 100,
    },
    quickActions: {
      flexDirection: 'row',
      paddingHorizontal: 20,
      marginBottom: 20,
      gap: 8,
    },
    quickAction: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: 12,
      paddingHorizontal: 8,
      borderRadius: 12,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 4,
      elevation: 2,
    },
    quickActionText: {
      fontSize: 12,
      fontWeight: '500',
      marginLeft: 4,
    },
    coursesList: {
      paddingHorizontal: 20,
    },
    courseCard: {
      marginBottom: 16,
    },
    courseCardContent: {
      borderRadius: 16,
      padding: 16,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
    },
    courseHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      marginBottom: 12,
    },
    courseIcon: {
      width: 48,
      height: 48,
      borderRadius: 24,
      justifyContent: 'center',
      alignItems: 'center',
      marginRight: 12,
    },
    courseTitleContainer: {
      flex: 1,
    },
    courseTitle: {
      fontSize: 16,
      fontWeight: 'bold',
      marginBottom: 4,
    },
    courseInstructor: {
      fontSize: 14,
    },
    bookmarkButton: {
      padding: 4,
    },
    courseSubtitle: {
      fontSize: 14,
      lineHeight: 20,
      marginBottom: 16,
    },
    courseProgressSection: {
      marginBottom: 16,
    },
    progressHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: 8,
    },
    progressLabel: {
      fontSize: 14,
      fontWeight: '600',
    },
    progressTime: {
      fontSize: 12,
    },
    progressBarContainer: {
      marginBottom: 8,
    },
    progressBarBackground: {
      height: 8,
      borderRadius: 4,
      overflow: 'hidden',
    },
    progressBarFill: {
      height: '100%',
      borderRadius: 4,
    },
    progressDetails: {
      flexDirection: 'row',
      justifyContent: 'space-between',
    },
    progressDetail: {
      flexDirection: 'row',
      alignItems: 'center',
    },
    progressDetailText: {
      fontSize: 12,
      marginLeft: 4,
    },
    courseActions: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      marginBottom: 12,
    },
    courseActionButton: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingHorizontal: 12,
      paddingVertical: 8,
      borderRadius: 8,
      flex: 1,
      marginHorizontal: 4,
      justifyContent: 'center',
    },
    courseActionText: {
      fontSize: 12,
      fontWeight: '600',
      marginLeft: 4,
    },
    nextLessonContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      padding: 12,
      borderRadius: 8,
    },
    nextLessonText: {
      fontSize: 12,
      marginLeft: 8,
      flex: 1,
    },
    completedCourseCard: {
      borderRadius: 16,
      padding: 16,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
    },
    completedCourseHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      marginBottom: 16,
    },
    completedBadge: {
      padding: 4,
    },
    completionDetails: {
      gap: 8,
      marginBottom: 16,
    },
    completionDetail: {
      flexDirection: 'row',
      alignItems: 'center',
    },
    completionDetailText: {
      fontSize: 14,
      marginLeft: 8,
    },
    completedActions: {
      flexDirection: 'row',
      gap: 8,
    },
    completedActionButton: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingHorizontal: 16,
      paddingVertical: 10,
      borderRadius: 8,
      flex: 1,
      justifyContent: 'center',
    },
    completedActionText: {
      fontSize: 14,
      fontWeight: '600',
      marginLeft: 6,
    },
    emptyState: {
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: 64,
      paddingHorizontal: 20,
    },
    emptyStateTitle: {
      fontSize: 20,
      fontWeight: 'bold',
      marginTop: 16,
      marginBottom: 8,
    },
    emptyStateText: {
      fontSize: 16,
      textAlign: 'center',
      marginBottom: 24,
    },
    emptyStateButton: {
      backgroundColor: '#667eea',
      paddingHorizontal: 32,
      paddingVertical: 12,
      borderRadius: 8,
      marginBottom: 8,
    },
    emptyStateButtonText: {
      color: '#ffffff',
      fontSize: 16,
      fontWeight: '600',
    },
    addCourseButton: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: '#10b981',
    },
    modalOverlay: {
      flex: 1,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      justifyContent: 'flex-end',
    },
    modalContainer: {
      flex: 1,
    },
    modalHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: 20,
      paddingVertical: 16,
      paddingTop: Platform.OS === 'ios' ? 40 : 16,
    },
    modalTitle: {
      fontSize: 18,
      fontWeight: 'bold',
      flex: 1,
      textAlign: 'center',
      marginHorizontal: 16,
    },
    modalContent: {
      flex: 1,
      padding: 20,
    },
    courseOverview: {
      alignItems: 'center',
      marginBottom: 24,
    },
    modalCourseIcon: {
      width: 80,
      height: 80,
      borderRadius: 40,
      justifyContent: 'center',
      alignItems: 'center',
      marginBottom: 16,
    },
    modalCourseTitle: {
      fontSize: 22,
      fontWeight: 'bold',
      textAlign: 'center',
      marginBottom: 8,
    },
    modalCourseInstructor: {
      fontSize: 16,
      textAlign: 'center',
      marginBottom: 16,
    },
    modalProgressContainer: {
      width: '100%',
    },
    modalProgressLabel: {
      fontSize: 16,
      fontWeight: '600',
      marginBottom: 8,
      textAlign: 'center',
    },
    modalProgressBar: {
      height: 12,
      borderRadius: 6,
      overflow: 'hidden',
    },
    modalProgressFill: {
      height: '100%',
      borderRadius: 6,
    },
    courseStats: {
      marginBottom: 24,
    },
    statRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      gap: 12,
    },
    statBox: {
      flex: 1,
      alignItems: 'center',
      padding: 16,
      borderRadius: 12,
    },
    statNumber: {
      fontSize: 18,
      fontWeight: 'bold',
      marginVertical: 8,
    },
    statLabel: {
      fontSize: 12,
    },
    modulesSection: {
      marginBottom: 24,
    },
    sectionTitle: {
      fontSize: 18,
      fontWeight: 'bold',
      marginBottom: 16,
    },
    moduleItem: {
      borderRadius: 12,
      padding: 16,
      marginBottom: 12,
    },
    moduleHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      marginBottom: 12,
    },
    moduleNumber: {
      width: 36,
      height: 36,
      borderRadius: 18,
      backgroundColor: '#667eea',
      justifyContent: 'center',
      alignItems: 'center',
      marginRight: 12,
    },
    moduleNumberText: {
      fontSize: 16,
      fontWeight: 'bold',
    },
    moduleInfo: {
      flex: 1,
    },
    moduleTitle: {
      fontSize: 16,
      fontWeight: '600',
      marginBottom: 4,
    },
    moduleDuration: {
      fontSize: 12,
    },
    completedIndicator: {
      padding: 4,
    },
    startButton: {
      padding: 4,
    },
    markCompleteButton: {
      paddingVertical: 8,
      paddingHorizontal: 16,
      borderRadius: 8,
      alignItems: 'center',
    },
    markCompleteText: {
      fontSize: 14,
      fontWeight: '600',
    },
    upcomingSection: {
      marginBottom: 24,
    },
    upcomingItem: {
      flexDirection: 'row',
      alignItems: 'center',
      padding: 16,
      borderRadius: 12,
      marginBottom: 12,
    },
    deadlineItem: {
      flexDirection: 'row',
      alignItems: 'center',
      padding: 16,
      borderRadius: 12,
    },
    upcomingInfo: {
      flex: 1,
      marginLeft: 12,
    },
    upcomingTitle: {
      fontSize: 14,
      fontWeight: '600',
      marginBottom: 4,
    },
    upcomingTime: {
      fontSize: 12,
    },
    modalActions: {
      flexDirection: 'row',
      padding: 20,
      gap: 12,
    },
    modalActionButton: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: 14,
      borderRadius: 12,
      flex: 1,
    },
    continueButton: {
      backgroundColor: '#667eea',
    },
    continueButtonText: {
      color: '#ffffff',
      fontSize: 16,
      fontWeight: '600',
      marginLeft: 8,
    },
    resourcesButton: {
      backgroundColor: '#10b981',
    },
    resourcesButtonText: {
      color: '#ffffff',
      fontSize: 16,
      fontWeight: '600',
      marginLeft: 8,
    },
    addCourseModal: {
      borderTopLeftRadius: 20,
      borderTopRightRadius: 20,
      padding: 20,
    },
    addCourseHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: 24,
    },
    addCourseTitle: {
      fontSize: 20,
      fontWeight: 'bold',
    },
    addCourseContent: {
      alignItems: 'center',
      marginBottom: 32,
    },
    addCourseSubtitle: {
      fontSize: 16,
      textAlign: 'center',
      marginVertical: 16,
    },
    courseCodeInput: {
      width: '100%',
      borderWidth: 1,
      borderRadius: 12,
      padding: 16,
      fontSize: 16,
      marginBottom: 12,
    },
    addCourseNote: {
      fontSize: 14,
      textAlign: 'center',
      fontStyle: 'italic',
    },
    addCourseActions: {
      flexDirection: 'row',
      gap: 12,
    },
    addCourseActionButton: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: 14,
      borderRadius: 12,
      flex: 1,
    },
    scanButton: {
      backgroundColor: '#f59e0b',
    },
    scanButtonText: {
      color: '#ffffff',
      fontSize: 16,
      fontWeight: '600',
      marginLeft: 8,
    },
    enrollButton: {
      backgroundColor: '#667eea',
    },
    enrollButtonDisabled: {
      backgroundColor: '#9ca3af',
    },
    enrollButtonText: {
      color: '#ffffff',
      fontSize: 16,
      fontWeight: '600',
      marginLeft: 8,
    },
    statsModal: {
      borderTopLeftRadius: 20,
      borderTopRightRadius: 20,
      maxHeight: height * 0.9,
    },
    statsHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingHorizontal: 20,
      paddingVertical: 16,
      borderBottomWidth: 1,
      borderBottomColor: darkMode ? '#4a5568' : '#e5e7eb',
    },
    statsTitle: {
      fontSize: 20,
      fontWeight: 'bold',
    },
    statsContent: {
      padding: 20,
    },
    overallStats: {
      marginBottom: 24,
    },
    overallStatsCard: {
      borderRadius: 16,
      padding: 24,
      alignItems: 'center',
    },
    overallStatsTitle: {
      fontSize: 16,
      color: '#ffffff',
      marginBottom: 8,
    },
    overallStatsPercentage: {
      fontSize: 48,
      fontWeight: 'bold',
      color: '#ffffff',
      marginBottom: 8,
    },
    overallStatsSubtitle: {
      fontSize: 14,
      color: 'rgba(255, 255, 255, 0.9)',
      textAlign: 'center',
    },
    detailedStats: {
      marginBottom: 24,
    },
    detailedStat: {
      flex: 1,
      alignItems: 'center',
      padding: 16,
      borderRadius: 12,
    },
    detailedStatNumber: {
      fontSize: 24,
      fontWeight: 'bold',
      marginVertical: 8,
    },
    detailedStatLabel: {
      fontSize: 12,
      textAlign: 'center',
    },
    weeklyProgress: {
      marginBottom: 24,
    },
    weeklyCourse: {
      marginBottom: 12,
    },
    weeklyCourseTitle: {
      fontSize: 14,
      marginBottom: 4,
    },
    weeklyProgressBar: {
      height: 8,
      backgroundColor: darkMode ? '#4a5568' : '#e5e7eb',
      borderRadius: 4,
      marginBottom: 4,
      overflow: 'hidden',
    },
    weeklyProgressFill: {
      height: '100%',
      borderRadius: 4,
    },
    weeklyProgressText: {
      fontSize: 12,
    },
    exportStatsButton: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: 16,
      borderRadius: 12,
      backgroundColor: darkMode ? '#4a5568' : '#f3f4f6',
    },
    exportStatsText: {
      fontSize: 16,
      fontWeight: '600',
      color: '#667eea',
      marginLeft: 8,
    },
  });

  if (loading) {
    return renderLoading();
  }

  return (
    <SafeAreaView style={styles.safeArea}>
      <StatusBar 
        barStyle="light-content" 
        backgroundColor="#667eea" 
      />
      
      {renderHeader()}
      {renderSearchBar()}
      
      <Animated.ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.contentContainer}
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            colors={['#667eea']}
            tintColor="#667eea"
          />
        }
        scrollEventThrottle={16}
        onScroll={Animated.event(
          [{ nativeEvent: { contentOffset: { y: scrollY } } }],
          { useNativeDriver: false }
        )}
      >
        {renderTabs()}
        {renderQuickActions()}
        
        <View style={styles.coursesList}>
          {filteredCourses.length === 0 ? (
            renderEmptyState()
          ) : activeTab === 'completed' ? (
            filteredCourses.map((course) => (
              <View key={course.id}>
                {renderCompletedCourseCard(course)}
              </View>
            ))
          ) : (
            filteredCourses.map((course) => (
              <View key={course.id}>
                {renderCourseCard(course)}
              </View>
            ))
          )}
        </View>
        
        {/* Spacer for bottom padding */}
        <View style={{ height: 100 }} />
      </Animated.ScrollView>
      
      {/* Floating Action Button */}
      <TouchableOpacity
        style={[styles.fab, { backgroundColor: '#667eea' }]}
        onPress={() => navigation.navigate('CourseCatalog')}
      >
        <MaterialCommunityIcons name="plus" size={24} color="#ffffff" />
      </TouchableOpacity>
      
      {/* Modals */}
      {renderCourseDetailsModal()}
      {renderAddCourseModal()}
      {renderStatsModal()}
    </SafeAreaView>
  );
};

export default MyCourses;